<template>
  <v-card>
    <v-list>
      <template v-if="safeDeadlines.length > 0">
        <DeadlineListItem
          v-for="deadline in safeDeadlines"
          :key="deadline.id"
          :deadline="deadline"
          :show_account="show_account"
        />
      </template>
      <v-alert v-else type="info" block class="ml-2 mr-2">
        {{ empty_message }}
      </v-alert>
    </v-list>
  </v-card>
</template>

<script>
import DeadlineListItem from "./DeadlineListItem.vue";
import { useDeadlinesStoreRefactor } from "../../stores/deadlinesStoreRefactor";
import { useFormsStore } from "../../stores/formsStore";
import { useAuthStoreRefactor } from "../../stores/authStoreRefactor";
import { useUsersStoreRefactor } from "../../stores/usersStoreRefactor";
import { computed, onMounted, watch } from "vue";
import { useRoute } from "vue-router";

export default {
  name: "DeadlineList",
  components: { DeadlineListItem },
  props: {
    deadlines: {
      type: Array,
      default: () => [],
    },
    account_id: [Number, String],
    show_account: Boolean,
    include_closed: {
      type: Boolean,
      default: false,
    },
    empty_message: {
      type: String,
      default: "Geen taken gevonden.",
    },
  },
  setup(props) {
    const deadlinesStore = useDeadlinesStoreRefactor();
    const formsStore = useFormsStore();
    const authStore = useAuthStoreRefactor();
    const route = useRoute();
    const usersStore = useUsersStoreRefactor();

    // Ensure deadlines are properly validated before rendering
    const safeDeadlines = computed(() => {
      // If using passed deadlines (like for a specific account or overview), trust caller's filtering
      if (
        props.deadlines !== undefined &&
        props.deadlines !== null &&
        Array.isArray(props.deadlines)
      ) {
        return props.deadlines.filter(
          (d) => d && typeof d === "object" && d.id,
        );
      }

      // If we have an account_id, filter deadlines from the store (open only)
      if (props.account_id) {
        return deadlinesStore.openDeadlines.filter((d) => {
          return (
            d &&
            typeof d === "object" &&
            d.id &&
            d.account_id === Number(props.account_id)
          );
        });
      }

      // Default: return open deadlines from the store
      return deadlinesStore.openDeadlines.filter((d) => {
        // Ensure each deadline is a valid object with at least an id
        return d && typeof d === "object" && d.id;
      });
    });

    // Get the highlighted deadline ID from query parameters
    const highlightedDeadlineId = computed(() => {
      return route.query.deadline ? Number(route.query.deadline) : null;
    });

    function addDeadline() {
      formsStore.create({ type: "deadline" });
    }

    // Function to handle highlighting a deadline
    const highlightDeadline = (deadlineId) => {
      if (!deadlineId) return;

      // Ensure the component is mounted and the DOM is updated
      setTimeout(() => {
        const element = document.getElementById(`d${deadlineId}`);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 500);
    };

    // Watch for changes in the highlighted deadline ID
    watch(highlightedDeadlineId, (newId) => {
      if (newId) {
        highlightDeadline(newId);
      }
    });

    onMounted(async () => {
      // Only fetch from store if deadlines prop is truly not provided (undefined/null)
      const hasPropProvided =
        props.deadlines !== undefined && props.deadlines !== null;
      if (!hasPropProvided) {
        await deadlinesStore.getAll();
      }

      // Initialize ActionCable for deadlines
      if (deadlinesStore.init) {
        deadlinesStore.init();
      }

      // Fetch users if not already loaded
      if (usersStore.users.length === 0) {
        await usersStore.getAll();
      }

      // Check if we need to highlight a deadline
      if (highlightedDeadlineId.value) {
        highlightDeadline(highlightedDeadlineId.value);
      }
    });

    return {
      safeDeadlines,
      addDeadline,
      highlightedDeadlineId,
    };
  },
};
</script>

<style scoped>
/* Add any custom styling here */
</style>
